<template>
  <div>
    <div class="bg-gray-50 border-2 border-gray-200 rounded-xl mb-1 px-3 py-1">
      <div class="text-sm">Showing stats for:</div>
      <div class="space-x-2 space-y-1">
        <select
          v-model="selectedTimeRange"
          class="h-10 !bg-white"
          @change="handleTimeRangeSelect"
        >
          <option
            v-for="option in timeRangeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <span class="whitespace-nowrap space-y-1">
          <input
            type="date"
            class="h-10 !bg-white"
            v-model="start"
            :max="yesterdayISO"
            :disabled="selectedTimeRange !== null" />
          -
          <input
            type="date"
            class="h-10 !bg-white"
            v-model="end"
            :max="todayISO"
            :disabled="selectedTimeRange !== null"
        /></span>
      </div>
    </div>
    <div v-if="dateRangeError" class="text-sm text-red-600">
      {{ dateRangeError }}
    </div>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from "vue";

const dateRangeError = ref(null);

const today = new Date();
const todayISO = today.toISOString().split("T")[0];

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
const yesterdayISO = yesterday.toISOString().split("T")[0];

const oneMonthAgo = new Date();
oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
const oneMonthAgoISO = oneMonthAgo.toISOString().split("T")[0];

const start = defineModel("start");
const end = defineModel("end");

const selectedTimeRange = ref(30);
const timeRangeOptions = ref([
  { label: "Past 7 days", value: 7 },
  { label: "Past 30 days", value: 30 },
  { label: "Past 60 days", value: 60 },
  { label: "Past 90 days", value: 90 },
  { label: "Past 6 months", value: 182 },
  { label: "Past year", value: 365 },
  { label: "Custom range", value: null },
]);

function handleTimeRangeSelect() {
  if (selectedTimeRange.value === null) {
    return;
  }
  end.value = todayISO;
  const newStart = new Date(end.value);
  newStart.setDate(newStart.getDate() - selectedTimeRange.value);
  start.value = newStart.toISOString().split("T")[0];
}

watch([start, end], async ([newStart, newEnd], [_oldStart, _oldEnd]) => {
  let adjustedStart = newStart;
  let adjustedEnd = newEnd;

  const newStartDate = new Date(newStart);
  const newEndDate = new Date(newEnd);

  // never allow future date range
  if (newStartDate > yesterday) {
    adjustedStart = yesterdayISO;
  }
  if (newEndDate > today) {
    adjustedEnd = todayISO;
  }

  if (adjustedStart >= adjustedEnd) {
    dateRangeError.value =
      "Please ensure the end date is after the start date.";
    return;
  }

  const timeDiff = new Date(adjustedEnd) - new Date(adjustedStart);
  const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

  // limit time range to one year
  if (daysDiff > 365) {
    dateRangeError.value = "Please ensure the date range is one year or less.";
    return;
  }

  dateRangeError.value = null;

  nextTick(async () => {
    if (start.value !== adjustedStart) {
      start.value = adjustedStart;
    }
    if (end.value !== adjustedEnd) {
      end.value = adjustedEnd;
    }
  });
});

onMounted(() => {
  start.value = oneMonthAgoISO;
  end.value = todayISO;
});
</script>
